import { getCognitoUserAttribute } from '@akin-home/auth/frontend';
import { useAuthUser, useSignOut } from '@akin-home/auth/react';
import { HabitatModel } from '@akin-home/habitats';
import { useEffect } from 'react';
import { create } from 'zustand';
import { shallow } from 'zustand/shallow';
import { useGetHabitatByHabitatID } from '../../hooks';

interface Props {
  children: JSX.Element;
}

interface HabitatStore {
  habitat?: HabitatModel;
  setHabitat(habitat?: HabitatModel): void;
}

export const useHabitatStore = create<HabitatStore>((set) => ({
  setHabitat(habitat) {
    set({ habitat });
  },
}));

export const HabitatProvider = (props: Props) => {
  /** First get auth */
  const auth_user = useAuthUser();
  const signOut = useSignOut();

  /** Get the related habitat */
  const { habitat, isLoading } = useGetHabitatByHabitatID(
    auth_user
      ? getCognitoUserAttribute(auth_user, 'custom:habitatID')
      : undefined
  );

  const setHabitat = useHabitatStore((state) => state.setHabitat);
  const habitatID = useHabitatStore((state) => state.habitat?.id);

  useEffect(() => {
    if (habitat) {
      setHabitat(habitat);
    } else if (!isLoading && !habitat) {
      /** The account is not set up right. Sign out */
      setHabitat(undefined);
      signOut();
    }
  }, [isLoading, habitat, setHabitat, signOut]);

  return habitatID ? props.children : null;
};

export const useHabitat = () =>
  useHabitatStore(
    ({ habitat }) => ({ habitat } as { habitat: HabitatModel }),
    shallow
  );

export const useHabitatID = () =>
  useHabitatStore((state) => state.habitat?.habitatID as string);

export const getHabitatID = () => useHabitatStore.getState().habitat?.habitatID;
